import type { State } from '../State';
import {
  FixedReward,
  FixedGemsReward,
  Match2Reward,
  TimedReward,
  isRandomReward,
  isRewardWithSubType,
  isTimedReward,
} from '../match2/rewards/match2-rewards.ruleset';
import { consolidateRewards } from '../match2/rewards/match2-rewards.getters';
import { shopBundleGetPackRewardSet } from '../match2/shopbundles/match2-shopbundles.getters';
import { ProductId } from '../iap/iap.ruleset';
import { match2BundleSaleGetPackRewardSet } from '../match2/bundlesale/match2-bundlesale.getters';
import { BundleSaleProductId } from '../match2/bundlesale/match2-bundlesale.ruleset';

interface UnclaimedMessagesData {
  coins: number;
  gems: number;
  newPetName: string;
  previousPetName: string;
  match2Rewards: (TimedReward | FixedReward)[];
  allowEditor: boolean;
  battlePass: boolean;
  resetPetNegativeEffects: boolean;
  superLollipopStreak: number;
  salePackPaidGems: number;
  salePackFreeGems: number;
  salePackProductIds: string[];
  setStarterPackPurchased: boolean;
  setPetStarterPackPurchased: boolean;
  bundleSalePackPaidGems: number;
  bundleSalePackFreeGems: number;
  bundleSalePackProductIds: string[];
}

interface UnclaimedMessagesPopupData {
  coins: number;
  gems: number;
  newPetName: string;
  previousPetName: string;
  match2Rewards: Match2Reward[];
  superLollipopStreak: number;
}

function isFixedGemReward(reward: Match2Reward): reward is FixedGemsReward {
  return reward.type === 'gems' && !isRandomReward(reward);
}

function getGemAmountFromRewards(rewards: Match2Reward[]) {
  return rewards.reduce((sum, reward) => {
    if (!isFixedGemReward(reward)) {
      return sum;
    }

    return sum + reward.amount;
  }, 0);
}

function filterOutGemRewards(rewards: Match2Reward[]) {
  return rewards.filter((reward) => reward.type !== 'gems');
}

/**
 * Merge a single match2Consumable into the accumulated rewards array.
 * Mutates `rewards` in place: finds an existing reward of the same
 * type/subType and adds to it, or creates a new entry.
 */
function accumulateMatch2Consumable(
  rewards: (TimedReward | FixedReward)[],
  consumable: NonNullable<State['admin'][number]['match2Consumables']>,
) {
  // Multipliers are always timed rewards.
  if (consumable.type === 'multiplier' && consumable.duration) {
    let existing = rewards.find((reward) => reward.type === 'multiplier') as
      | TimedReward
      | undefined;

    if (!existing) {
      existing = {
        type: consumable.type,
        subType: consumable.subType,
        amount: 2, // currently fixed to x2
        duration: 0,
      } as TimedReward;
      rewards.push(existing);
    }

    existing.duration += consumable.duration;
    return;
  }

  // Accumulate fixed reward (by amount).
  if (consumable.amount) {
    let existing = rewards.find((reward) => {
      const hasAmount = !isTimedReward(reward) && !isRandomReward(reward);
      const typeMatched = reward.type === consumable.type;
      const existingSubType = isRewardWithSubType(reward)
        ? reward.subType
        : undefined;
      const subTypeMatched = consumable.subType === existingSubType;
      return hasAmount && typeMatched && subTypeMatched;
    }) as FixedReward | undefined;

    if (!existing) {
      existing = {
        type: consumable.type,
        subType: consumable.subType,
        amount: 0,
      } as FixedReward;
      rewards.push(existing);
    }

    // Some rewards do not have amount, like the superLollipop reward
    if ('amount' in existing && typeof existing.amount === 'number') {
      existing.amount += consumable.amount;
    }
  }

  // Accumulate timed reward (by duration).
  if (consumable.duration) {
    let existing = rewards.find((reward) => {
      const hasDuration = isTimedReward(reward);
      const typeMatched = reward.type === consumable.type;
      const existingSubType = isRewardWithSubType(reward)
        ? reward.subType
        : undefined;
      const subTypeMatched = consumable.subType === existingSubType;
      return hasDuration && typeMatched && subTypeMatched;
    }) as TimedReward | undefined;

    if (!existing) {
      existing = {
        type: consumable.type,
        subType: consumable.subType,
        duration: 0,
      } as TimedReward;
      rewards.push(existing);
    }

    existing.duration += consumable.duration;
  }
}

function createEmptyUnclaimedMessagesData(): UnclaimedMessagesData {
  return {
    coins: 0,
    gems: 0,
    newPetName: '',
    previousPetName: '',
    match2Rewards: [] as (TimedReward | FixedReward)[],
    allowEditor: false,
    battlePass: false,
    resetPetNegativeEffects: false,
    superLollipopStreak: 0,
    salePackPaidGems: 0,
    salePackFreeGems: 0,
    salePackProductIds: [] as string[],
    setStarterPackPurchased: false,
    setPetStarterPackPurchased: false,
    bundleSalePackPaidGems: 0,
    bundleSalePackFreeGems: 0,
    bundleSalePackProductIds: [] as string[],
  };
}

/**
 * Reduce all unclaimed admin messages into a claim payload.
 */
export function getUnclaimedMessages(state: State): UnclaimedMessagesData {
  return state.admin
    .filter((message) => !message.claimed)
    .reduce((acc, item) => {
      acc.coins += item.coins ?? 0;
      acc.gems += item.freeGems ?? 0;
      acc.newPetName = item.newPetName ?? acc.newPetName;
      acc.previousPetName = item.previousPetName ?? acc.previousPetName;
      acc.allowEditor = item.allowEditor ?? acc.allowEditor;
      acc.battlePass = item.battlePass || acc.battlePass;
      acc.resetPetNegativeEffects =
        item.resetPetNegativeEffects || acc.resetPetNegativeEffects;
      acc.superLollipopStreak += item.superLollipopStreak ?? 0;

      // Aggregate sale pack data
      if (item.salePack) {
        acc.salePackPaidGems += item.salePack.paidGems ?? 0;
        acc.salePackFreeGems += item.salePack.freeGems ?? 0;
        if (item.salePack.productId) {
          acc.salePackProductIds.push(item.salePack.productId);
        }
        acc.setStarterPackPurchased =
          acc.setStarterPackPurchased ||
          (item.salePack.setStarterPackPurchased ?? false);
        acc.setPetStarterPackPurchased =
          acc.setPetStarterPackPurchased ||
          (item.salePack.setPetStarterPackPurchased ?? false);
      }

      if (item.bundleSalePack) {
        acc.bundleSalePackPaidGems += item.bundleSalePack.paidGems ?? 0;
        acc.bundleSalePackFreeGems += item.bundleSalePack.freeGems ?? 0;
        acc.bundleSalePackProductIds.push(
          ...item.bundleSalePack.reimbursableProductIds,
        );
      }

      if (item.match2Consumables) {
        accumulateMatch2Consumable(acc.match2Rewards, item.match2Consumables);
      }

      return acc;
    }, createEmptyUnclaimedMessagesData());
}

/**
 * Build popup payload for launch flow from unclaimed admin messages.
 * Includes sale/bundle sale gems and rewards.
 */
export function getUnclaimedMessagesPopupData(
  state: State,
  now: number,
): UnclaimedMessagesPopupData {
  const unclaimed = getUnclaimedMessages(state);
  const salePackRewards = unclaimed.salePackProductIds.flatMap((productId) =>
    shopBundleGetPackRewardSet(productId as ProductId, now),
  );
  const bundleSaleRewards = unclaimed.bundleSalePackProductIds.flatMap(
    (productId) =>
      match2BundleSaleGetPackRewardSet(productId as BundleSaleProductId, now),
  );
  const salePackRewardGems = getGemAmountFromRewards(salePackRewards);
  const bundleSaleRewardGems = getGemAmountFromRewards(bundleSaleRewards);
  const salePackRewardsWithoutGems = filterOutGemRewards(salePackRewards);
  const bundleSaleRewardsWithoutGems = filterOutGemRewards(bundleSaleRewards);

  return {
    coins: unclaimed.coins,
    gems:
      unclaimed.gems +
      unclaimed.salePackPaidGems +
      unclaimed.salePackFreeGems +
      unclaimed.bundleSalePackPaidGems +
      unclaimed.bundleSalePackFreeGems +
      salePackRewardGems +
      bundleSaleRewardGems,
    newPetName: unclaimed.newPetName,
    previousPetName: unclaimed.previousPetName,
    match2Rewards: consolidateRewards([
      ...unclaimed.match2Rewards,
      ...salePackRewardsWithoutGems,
      ...bundleSaleRewardsWithoutGems,
    ]),
    superLollipopStreak: unclaimed.superLollipopStreak,
  };
}

/**
 * Check if there are unclaimed messages.
 */
export function hasUnclaimedMessages(state: State): boolean {
  return state.admin.some((message) => !message.claimed);
}
